import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AdminProvider } from "./context/AdminContext";
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { CookieConsent } from "./components/CookieConsent";
import { BackToTop } from "./components/BackToTop";
import { SEO } from "./components/SEO";
import { Home } from "./pages/Home";
import { Pricing } from "./pages/Pricing";
import { GalleryDetail } from "./pages/GalleryDetail";
import { BlogDetail } from "./pages/BlogDetail";
import { BlogList } from "./pages/BlogList";
import { ClientLogin } from "./pages/ClientLogin";
import { PrivateArea } from "./pages/PrivateArea";
import { MaintenancePage } from "./pages/MaintenancePage";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminLayout } from "./pages/AdminLayout";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminBrokenImages } from "./pages/admin/AdminBrokenImages";
import { AdminGalleries } from "./pages/admin/AdminGalleries";
import { AdminServices } from "./pages/admin/AdminServices";
import { AdminBlog } from "./pages/admin/AdminBlog";
import { AdminTestimonials } from "./pages/admin/AdminTestimonials";
import { AdminClients } from "./pages/admin/AdminClients";
import { AdminCalendar } from "./pages/admin/AdminCalendar";
import { AdminSharedDocuments } from "./pages/admin/AdminSharedDocuments";
import { AdminPrivateFiles } from "./pages/admin/AdminPrivateFiles";
import { AdminSecurity } from "./pages/admin/AdminSecurity";
import { AdminHero } from "./pages/admin/AdminHero";
import { AdminAnalytics } from "./pages/admin/AdminAnalytics";
import { AdminDataMigration } from "./pages/admin/AdminDataMigration";
import { AdminDownloadLogs } from "./pages/admin/AdminDownloadLogs";
import { AdminNewsletter } from "./pages/admin/AdminNewsletter";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { LegalNotice } from "./pages/LegalNotice";
import { CookiePolicy } from "./pages/CookiePolicy";
import { useAnalytics } from "./hooks/useAnalytics";
import { useImageProtection } from "./hooks/useImageProtection";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seed data on first load
const seedData = async () => {
  try {
    await fetch(`${API}/seed`, { method: 'POST' });
  } catch (error) {
    console.log('Seed already done or error:', error);
  }
};

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

// Analytics tracker component
function AnalyticsTracker() {
  useAnalytics();
  return null;
}

// Image protection component
function ImageProtection() {
  useImageProtection();
  return null;
}

function AppRouter({ maintenance }) {
  const location = useLocation();

  // Admin routes always accessible (even in maintenance)
  if (location.pathname.startsWith('/admin')) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="galleries" element={<AdminGalleries />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="shared-documents" element={<AdminSharedDocuments />} />
          <Route path="private-files" element={<AdminPrivateFiles />} />
          <Route path="security" element={<AdminSecurity />} />
          <Route path="hero" element={<AdminHero />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="data-migration" element={<AdminDataMigration />} />
          <Route path="download-logs" element={<AdminDownloadLogs />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="broken-images" element={<AdminBrokenImages />} />
        </Route>
      </Routes>
    );
  }

  // Show maintenance page for public routes if enabled
  if (maintenance?.enabled) {
    return <MaintenancePage message={maintenance.message} endTime={maintenance.end_time} />;
  }

  // Client login page without navbar/footer
  if (location.pathname === '/client/login') {
    return (
      <Routes>
        <Route path="/client/login" element={<ClientLogin />} />
      </Routes>
    );
  }

  return (
    <>
      <AnalyticsTracker />
      <ImageProtection />
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/gallery/:id" element={<GalleryDetail />} />
        <Route path="/blog/:id" element={<BlogDetail />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/private" element={<PrivateArea />} />
        <Route path="/privacidad" element={<PrivacyPolicy />} />
        <Route path="/aviso-legal" element={<LegalNotice />} />
        <Route path="/politica-cookies" element={<CookiePolicy />} />
      </Routes>
      <Footer />
      <CookieConsent />
      <BackToTop />
    </>
  );
}

function App() {
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    seedData();
    checkMaintenance();
    
    // Check maintenance status periodically (every 30 seconds)
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkMaintenance = async () => {
    try {
      const response = await fetch(`${API}/maintenance`);
      if (response.ok) {
        const data = await response.json();
        setMaintenance(data);
      }
    } catch (error) {
      console.log('Error checking maintenance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AdminProvider>
          <BrowserRouter>
            <SEO />
            <AppRouter maintenance={maintenance} />
          </BrowserRouter>
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))'
              }
            }}
          />
        </AdminProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
